import React, { useCallback, useMemo } from 'react'

import { useReactFlow } from '@xyflow/react'

import Box from 'components_new/atoms/Box'

import Node, { InputType } from 'components_new/molecules/Node'
import FieldSelect, {
  FieldOption
} from 'components_new/molecules/Node/FieldSelect'
import SideBadge from 'components_new/molecules/Node/SideBadge'

import NodeType from 'types/transformation/enums/NodeType'
import {
  LeftJoinNode,
  NodeSchema,
  Node as TNode
} from 'types/transformation/types/nodes'

import { pruneRef, useInputSchemas, useIsInvalid, usePruneRefs, useColumnOrigins } from './hooks'

interface LeftJoinNodeProps {
  id: string
  data: LeftJoinNode
  selected: boolean
}

const LeftJoin = (props: LeftJoinNodeProps) => {
  const { id, data, selected } = props

  const { setNodes } = useReactFlow<TNode>()

  const isInvalid = useIsInvalid(data)
  const inputSchemas = useInputSchemas({
    left: data.left_node,
    right: data.right_node
  })

  const originByNode = useColumnOrigins()

  const toFieldOptions = useCallback(
    (schema: NodeSchema[]): FieldOption[] =>
      schema.map((column) => ({
        id: column.id,
        name: column.name,
        type: column.type,
        nodeIndex: column.reference_node_index,
        origin: originByNode.get(column.reference_node) ?? null
      })),
    [originByNode]
  )

  const leftOptions = useMemo(
    () => toFieldOptions(inputSchemas.left),
    [toFieldOptions, inputSchemas.left]
  )
  const rightOptions = useMemo(
    () => toFieldOptions(inputSchemas.right),
    [toFieldOptions, inputSchemas.right]
  )

  // input schema has changed, validate references
  usePruneRefs<LeftJoinNode>(
    id,
    (nodeData) => ({
      ...nodeData,
      left_column: pruneRef(nodeData.left_column, inputSchemas.left),
      right_column: pruneRef(nodeData.right_column, inputSchemas.right)
    }),
    [inputSchemas.left, inputSchemas.right]
  )

  const handleSelected = useCallback(
    (value: string, column: 'left_column' | 'right_column') => {
      setNodes((existingNodes) =>
        existingNodes.map((node) =>
          node.id === id
            ? { ...node, data: { ...node.data, [column]: value } }
            : node
        )
      )
    },
    [setNodes, id]
  )

  return (
    <Node
      error={isInvalid}
      icon="JoinLeft"
      inputType={InputType.DOUBLE}
      title={'Left join'}
      selected={selected}
      nodeNumber={data.index}
      type={NodeType.LEFT_JOIN}
    >
      <Box sx={{ width: '272px' }}>
        <Box
          sx={{ alignItems: 'center', display: 'flex', gap: '7px', mb: 0.5 }}
        >
          <SideBadge label="L" />
          <FieldSelect
            value={data.left_column}
            options={leftOptions}
            onChange={(val) => handleSelected(val, 'left_column')}
            placeholder="Välj nyckel"
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: '7px' }}>
          <SideBadge label="R" />
          <FieldSelect
            value={data.right_column}
            options={rightOptions}
            onChange={(val) => handleSelected(val, 'right_column')}
            placeholder="Välj nyckel"
            sx={{ flex: 1, minWidth: 0 }}
          />
        </Box>
      </Box>
    </Node>
  )
}

export default LeftJoin
